import { Button } from '@/components/ui/button';
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import { labels } from '@/locales/en.label';

import { Switch } from '@/components/ui/switch';
import { useUpdateUserMutation } from '@/features/users/gql/_gen_/user.gql';
import type { User } from '@/graphql/gql-types';
import type React from 'react';
import { useState } from 'react';
import { RoleOptions } from '.';

interface EditUserPopupProps {
	children: React.ReactNode;
	user: User;
}
export function EditUserPopup({ children, user }: EditUserPopupProps) {
	const [open, setOpen] = useState(false);
	const [formData, setFormData] = useState<User>({ ...user });
	const [updateUser] = useUpdateUserMutation();
	const handleSave = () => {
		// make an API call to update the user
		console.log('Saving user:', { ...formData });
		updateUser({
			variables: {
				id: formData.id!,
				super_admin: formData.super_admin,
				...formData,
			},
			onCompleted() {
				setOpen(false);
			},
		});
	};

	return (
		<Dialog open={open} onOpenChange={setOpen}>
			<DialogTrigger asChild>{children}</DialogTrigger>
			<DialogContent className='sm:tw-max-w-[500px]'>
				<DialogHeader>
					<DialogTitle>{labels.admin.user_table.modal.edit.header}</DialogTitle>
					<DialogDescription>
						{labels.admin.user_table.modal.edit.description}
					</DialogDescription>
				</DialogHeader>
				<div className='tw-grid tw-gap-4 tw-py-4'>
					<div className='tw-grid tw-grid-cols-4 tw-items-center tw-gap-4'>
						<Label htmlFor='name' className='tw-text-right'>
							{labels.admin.user_table.header.name}
						</Label>
						<Input
							id='name'
							value={formData.name}
							className='tw-col-span-3'
							disabled
						/>
					</div>
					<div className='tw-grid tw-grid-cols-4 tw-items-center tw-gap-4'>
						<Label htmlFor='email' className='tw-text-right'>
							{labels.admin.user_table.header.email}
						</Label>
						<Input
							id='email'
							value={formData.email}
							className='tw-col-span-3'
							disabled
						/>
					</div>
					<div className='tw-grid tw-grid-cols-4 tw-items-center tw-gap-4'>
						<Label htmlFor='role' className='tw-text-right'>
							{labels.admin.user_table.header.role}
						</Label>
						<Select
							value={formData.role}
							onValueChange={(value) =>
								setFormData({ ...formData, role: value as User['role'] })
							}
						>
							<SelectTrigger className='tw-col-span-3'>
								<SelectValue placeholder='Select a role' />
							</SelectTrigger>
							<SelectContent>
								{RoleOptions.map((option) => (
									<SelectItem key={option.value} value={option.value}>
										{option.label}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					</div>
					<div className='tw-grid tw-grid-cols-4 tw-items-center tw-gap-4'>
						<Label htmlFor='super_admin' className='tw-text-right'>
							Super Admin
						</Label>
						<div className='tw-col-span-3 tw-flex tw-items-center tw-space-x-2'>
							<Switch
								id='super_admin'
								checked={formData.super_admin}
								onCheckedChange={(checked: any) =>
									setFormData({ ...formData, super_admin: checked })
								}
							/>
						</div>
					</div>
					<div className='tw-grid tw-grid-cols-4 tw-items-center tw-gap-4'>
						<Label className='tw-text-right'>
							{labels.admin.user_table.text.properties}
						</Label>
						<div className='tw-col-span-3 tw-space-y-2'>
							<div className='tw-flex tw-flex-wrap tw-gap-2 tw-mb-2'>
								{user?.prop_id?.length === 0 ? (
									<div className='text-sm text-muted-foreground'>
										{labels.admin.user_table.text.no_properties_assigned}
									</div>
								) : (
									user?.prop_id?.map((property) => (
										<div key={property}>{property}</div>
									))
								)}
							</div>
						</div>
					</div>
				</div>
				<DialogFooter>
					<Button variant='outline' onClick={() => setOpen(false)}>
						{labels.admin.user_table.btn.cancel}
					</Button>
					<Button onClick={handleSave}>
						{labels.admin.user_table.btn.save}
					</Button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
